import { ShortfyEvent } from "../../types/event.types";
import { EventRegistration, MealPreference, RegistrationStatus } from "../../types/shared.types";


type RegistrationStats = {
  totalRegistrations: number;
  confirmedCount: number;
  tentativeCount: number;
  declinedCount: number;
  cancelledCount: number;
  attendedCount: number;
  mealStats: {
    none: number;
    meat: number;
    vegetarian: number;
    vegan: number;
    flex: number;
    noMeal: number;
  };
  capacityRemaining: number;
  totalParticipants: number;
  totalAdults: number;
  totalChildren: number;
};

const escapeCsv = (val: unknown) => {
  const v = (val ?? "").toString();
  if (v.includes(",") || v.includes('"') || v.includes("\n")) {
    return `"${v.replace(/"/g, '""')}"`;
  }
  return v;
};

const formatDateTime = (isoString: string) => {
  const d = new Date(isoString);
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDate = (isoString: string) => {
  const d = new Date(isoString);
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const getDurationText = (startIso: string, endIso: string) => {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const durationMs = end.getTime() - start.getTime();
  const durationHours = Math.floor(durationMs / (1000 * 60 * 60));
  const durationMinutes = Math.floor(
    (durationMs % (1000 * 60 * 60)) / (1000 * 60),
  );
  const durationDays = Math.floor(durationHours / 24);

  if (durationDays > 0) {
    let txt = `${durationDays} day${durationDays > 1 ? "s" : ""}`;
    const remHours = durationHours % 24;
    if (remHours > 0) txt += `, ${remHours} hour${remHours > 1 ? "s" : ""}`;
    return txt;
  }
  if (durationHours > 0) {
    let txt = `${durationHours} hour${durationHours > 1 ? "s" : ""}`;
    if (durationMinutes > 0)
      txt += `, ${durationMinutes} minute${durationMinutes > 1 ? "s" : ""}`;
    return txt;
  }
  return `${durationMinutes} minute${durationMinutes > 1 ? "s" : ""}`;
};

// NEW: maps backend RegistrationStatus to display labels for the export
const participationStatusLabel = (status: RegistrationStatus): string => {
  switch (status) {
    case RegistrationStatus.Registered: return "Registered";
    case RegistrationStatus.Attended:   return "Checked-in";
    case RegistrationStatus.Cancelled:  return "Cancelled";
    case RegistrationStatus.Declined:   return "Declined";
    case RegistrationStatus.Tentative:  return "Tentative";
    default:                            return status;
  }
};

const mealLabelShort = (pref: MealPreference) => {
  switch (pref) {
    case MealPreference.Meat:
      return "Meat";
    case MealPreference.Vegetarian:
      return "Veg";
    case MealPreference.Vegan:
      return "Vegan";
    case MealPreference.Flex:
      return "Flex";
    case MealPreference.NoMeal:
      return "No Meal Required";
    default:
      return "-";
  }
};

export const exportEventToCSVReport = async (
  event: ShortfyEvent,
): Promise<void> => {
  const backendUrlRaw = process.env.REACT_APP_BACKEND_URL || "";
  const backendUrl = backendUrlRaw.startsWith("http")
    ? backendUrlRaw.replace(/\/$/, "")
    : window.location.origin;

  // OLD: used authToken from localStorage with Authorization header — backend ignores this
  // const token = localStorage.getItem("authToken") || "";
  // headers["Authorization"] = `Bearer ${token}`;

  // NEW: backend requireAuth middleware reads client-principal-user-id from sessionStorage
  const userId = sessionStorage.getItem("userOid") || "";
  const headers: HeadersInit = { "Content-Type": "application/json" };
  if (userId) headers["client-principal-user-id"] = userId;

  let registrations: EventRegistration[] = [];
  try {
    // OLD: `${backendUrl}/api/registrations/${event.eventId}` — excluded Declined participants
    // NEW: includeDeclined=true ensures ALL participants regardless of status are returned
    const res = await fetch(
      `${backendUrl}/api/registrations/${event.eventId}?includeDeclined=true`,
      {
        credentials: "include",
        headers,
      },
    );
    if (res.ok) {
      const json = await res.json();
      registrations = json.data || [];
    } else {
      console.error("[CSV Export] Registrations fetch failed:", res.status, await res.text());
    }
  } catch (err) {
    console.error("[CSV Export] Registrations fetch error:", err);
  }

  let stats: RegistrationStats = {
    totalRegistrations: event.currentRegistrations || 0,
    confirmedCount: event.currentRegistrations || 0,
    tentativeCount: 0,
    declinedCount: 0,
    cancelledCount: 0,
    attendedCount: 0,
    mealStats: { none: 0, meat: 0, vegetarian: 0, vegan: 0, flex: 0, noMeal: 0 },
    capacityRemaining: event.maxCapacity - (event.currentRegistrations || 0),
    totalParticipants: 0,
    totalAdults: 0,
    totalChildren: 0,
  };

  try {
    const res = await fetch(
      `${backendUrl}/api/registrations/${event.eventId}/stats`,
      {
        credentials: "include",
        headers,
      },
    );
    if (res.ok) {
      const json = await res.json();
      if (json.data) stats = json.data;
    }
  } catch {}

  const hasEventEnded = new Date(event.eventEndDate) < new Date();

  // OLD: only confirmed attendees were exported
  // const confirmed = registrations.filter(
  //   (r) =>
  //     r.status === RegistrationStatus.Registered ||
  //     r.status === RegistrationStatus.Attended,
  // );

  // NEW: export all participants regardless of status
  const participants = registrations;

  const lines: string[] = [];

  lines.push("Event Details");
  lines.push(["Field", "Value"].map(escapeCsv).join(","));
  lines.push(["Name", event.eventName].map(escapeCsv).join(","));
  lines.push(
    ["Start", formatDateTime(event.eventStartDate)].map(escapeCsv).join(","),
  );
  lines.push(
    ["End", formatDateTime(event.eventEndDate)].map(escapeCsv).join(","),
  );
  lines.push(["Location", event.location].map(escapeCsv).join(","));
  lines.push(
    ["Duration", getDurationText(event.eventStartDate, event.eventEndDate)]
      .map(escapeCsv)
      .join(","),
  );
  lines.push(
    ["Deadline", formatDate(event.registrationDeadline)]
      .map(escapeCsv)
      .join(","),
  );
  lines.push(["Status", event.status].map(escapeCsv).join(","));
  lines.push(
    ["Capacity", `${event.currentRegistrations} / ${event.maxCapacity}`]
      .map(escapeCsv)
      .join(","),
  );

  lines.push("");

  lines.push("Registration Statistics");

  const statHeaders = hasEventEnded
    ? ["Total", "Confirmed", "Tentative", "Attended", "Declined", "Cancelled"]
    : ["Total", "Confirmed", "Tentative", "Declined", "Cancelled"];

  const statValues = hasEventEnded
    ? [
        stats.totalRegistrations,
        stats.confirmedCount,
        stats.tentativeCount,
        stats.attendedCount,
        stats.declinedCount,
        stats.cancelledCount,
      ]
    : [
        stats.totalRegistrations,
        stats.confirmedCount,
        stats.tentativeCount,
        stats.declinedCount,
        stats.cancelledCount,
      ];

  lines.push(statHeaders.map(escapeCsv).join(","));
  lines.push(statValues.map(escapeCsv).join(","));

  lines.push("");

  if (event.mealOptionsEnabled) {
    lines.push("Meal Preferences");
    lines.push(
      ["Meat", "Vegetarian", "Vegan", "Flex", "No Meal Required", "No Pref."]
        .map(escapeCsv)
        .join(","),
    );
    lines.push(
      [
        stats.mealStats.meat,
        stats.mealStats.vegetarian,
        stats.mealStats.vegan,
        stats.mealStats.flex,
        stats.mealStats.noMeal,
        stats.mealStats.none,
      ]
        .map(escapeCsv)
        .join(","),
    );
    lines.push("");
  }

  // Participant breakdown (when additional guests enabled)
  if (event.additionalGuestsEnabled) {
    lines.push("Participant Breakdown");
    lines.push(
      ["Total Participants", "Partners", "Additional Adults", "Additional Children"]
        .map(escapeCsv)
        .join(","),
    );
    lines.push(
      [
        stats.totalParticipants,
        stats.confirmedCount + stats.attendedCount,
        stats.totalAdults,
        stats.totalChildren,
      ]
        .map(escapeCsv)
        .join(","),
    );
    lines.push("");
  }

  // NEW: all participants with Name, BU, Sector, Participation Status, Meal (if enabled), Adults/Children (if enabled), Notes
  lines.push(`Participants (${participants.length})`);

  const participantHeaders = [
    "Name",
    "BU",
    "Sector",
    "Participation Status",
    ...(event.mealOptionsEnabled ? ["Meal Preference"] : []),
    ...(event.additionalGuestsEnabled ? ["Addl. Adults", "Addl. Children"] : []),
    "Notes",
  ];
  lines.push(participantHeaders.map(escapeCsv).join(","));

  participants.forEach((r) => {
    const row = [
      r.userName,
      r.cluster || "-",
      r.sector || "-",
      participationStatusLabel(r.status),
      ...(event.mealOptionsEnabled ? [mealLabelShort(r.mealPreference)] : []),
      ...(event.additionalGuestsEnabled ? [r.additionalAdults ?? 0, r.additionalChildren ?? 0] : []),
      (r.notes ?? "").trim() || "-",
    ];
    lines.push(row.map(escapeCsv).join(","));
  });

  const csvContent = lines.join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

  const a = document.createElement("a");
  const url = URL.createObjectURL(blob);
  a.href = url;

  const fileName = `CGI_Event_${event.eventName.replace(/[^a-z0-9]/gi, "_")}_${
    new Date().toISOString().split("T")[0]
  }.csv`;

  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
